import {
  CardOutline,
  DocumentTextOutline,
  ShieldCheckmarkOutline,
} from "@raresail/react-ionicons";
import { useState } from "react";
import { App } from "antd";
import { downloadPdf } from "../../utils/pdfDownload";
import { WalletIcon } from "../../assets/svg/Svgs";
import { Button } from "../../components/common/Button";
import { useAuth } from "../../components/context/AuthContext";
import { DetailsView } from "../../components/details/DetailsView";
import {
  IconBox,
  ListItemWrapper,
  RiskLevelTag,
  StatusTag,
} from "../../components/elements/Elements";
import { ListHandler } from "../../components/lists/ListHandler";
import { API_LOANS } from "../../constants/apis";
import {
  ACCEPT_OFFER,
  ACTIVE,
  BANK_ACCOUNT,
  CASH_AT_AGENT,
  DEVICE_ORDERS,
  EARLY_SETTLE,
  FUNDED,
  INSTALLMENTS,
  LOANS,
  MOBILE_MONEY,
  PAID,
  PENDING,
  REPAY,
  SELECT,
} from "../../constants/constants";
import {
  addSelectOption,
  currencyFormat,
  formatDateTime,
  stringify,
} from "../../utils/helpers";
import { openBottomSheet, openModalForm } from "../../utils/modalUtils";
import { searchFilterField } from "../../constants/options";

export const useLoansList = () => {
  const ListItem = ({ data }: { data: any }) => {
    const {
      id,
      purpose,
      amount,
      status,
      payment_method,
      repayment_frequency,
      is_fully_funded,
      loan_type,
    } = data || {};
    const label = stringify(purpose);
    const isDevice = loan_type === "device_finance";

    return (
      <ListItemWrapper
        className="flex gap15"
        onClick={() =>
          openBottomSheet({
            component: "tabs",
            title: "Loan Details",
            id,
            source: LOANS,
            footer: LoanActionFooter,
          })
        }
      >
        <div className="flexCenter">
          <IconBox icon={WalletIcon} size={20} />
        </div>
        {/* MID */}
        <div className="flexGrow">
          <div
            className="font13 semiBold textDark capitalize"
            style={{ lineHeight: 1.3 }}
          >
            {label}
          </div>
          <div className="font12 main capitalize flexNullCenter gap5">
            <CardOutline />
            {isDevice ? "Device Finance" : stringify(payment_method)}
          </div>
          <div className="font11 grey capitalize">
            {stringify(repayment_frequency)}
          </div>
        </div>
        {/* END */}
        <div className="textEnd">
          <div className="font13 semiBold textDark mb5">
            {currencyFormat(amount)}
          </div>
          <div className="flexNullEnd gap5">
            {is_fully_funded && status !== PAID && (
              <StatusTag status={FUNDED} icon={ShieldCheckmarkOutline} />
            )}
            <StatusTag status={status} />
          </div>
        </div>
      </ListItemWrapper>
    );
  };

  const filters = [
    searchFilterField,
    {
      field: "payment_method",
      type: SELECT,
      options: [MOBILE_MONEY, BANK_ACCOUNT, CASH_AT_AGENT].map((v) =>
        addSelectOption(v)
      ),
    },
    {
      field: "repayment_frequency",
      type: SELECT,
      options: ["weekly", "monthly"].map((v) =>
        addSelectOption(v)
      ),
    },
    {
      field: "status",
      type: SELECT,
      options: [
        PENDING,
        ACTIVE,
        PAID,
        "repaid",
        FUNDED,
        "defaulted",
        "rejected",
      ].map((v) => addSelectOption(v)),
    },
  ];

  const viewData = (data: any) => {
    const {
      purpose,
      amount,
      status,
      remaining_balance,
      repayment_frequency,
      payment_method,
      loan_id,
      loan_type,
      loan_duration,
      interest_rate,
      interest_amount,
      created_at,
      total_units,
      risk_tier,
      is_fully_funded,
      funding_model,
    } = data || {};
    return {
      purpose,
      loan_type:
        loan_type === "device_finance" ? "Device Finance" : "Business Cash Loan",
      amount: currencyFormat(amount),
      interest_amount: currencyFormat(interest_amount),
      remaining_balance: (
        <div className={`${remaining_balance > 0 && "red bold"}`}>
          {currencyFormat(remaining_balance)}
        </div>
      ),
      status: <StatusTag status={status} />,
      loan_ID: loan_id,
      repayment_frequency,
      payment_method,
      loan_duration: `${loan_duration} days`,
      interest_rate: `${interest_rate}%`,
      funding_model: funding_model === "on_balance_sheet" ? "Direct" : "P2P",
      total_units,
      risk_tier: <RiskLevelTag value={risk_tier} />,
      is_fully_funded,
      created_at: formatDateTime(created_at),
    };
  };

  const detailsTabs = ({ ...props }) => {
    return [
      {
        key: "1",
        label: "details",
        children: (
          <DetailsView {...props} showTitle={false} enablePadding={false} />
        ),
      },
      {
        key: "2",
        label: "schedule",
        children: (
          <ListHandler
            key={props?.id}
            enablePadding={false}
            source={INSTALLMENTS}
            state={{ id: props?.id }}
          />
        ),
      },
    ];
  };

  return {
    ListItem,
    api: API_LOANS,
    filters,
    viewData,
    action: { onClick: () => openModalForm({ source: LOANS }) },
    detailsTabs,
  };
};

const LoanActionFooter = ({ details }: { details?: any }) => {
  const { user } = useAuth();
  const { message } = App.useApp();
  const [downloading, setDownloading] = useState<null | "contract" | "schedule">(null);

  if (!details || user?.id !== details?.user?.id) return null;

  const { status, offer_accepted, loan_type, loan_id, id } = details;
  const isDevice = loan_type === "device_finance";

  // Contract becomes available once an offer has been accepted / loan is funded
  // or active. Pending/under-review loans don't yet have a binding contract.
  const contractAvailable =
    offer_accepted ||
    [ACTIVE, PAID, FUNDED, "completed", "repaid", "disbursed", "defaulted"].includes(status);

  const handleDownload = async (kind: "contract" | "schedule") => {
    setDownloading(kind);
    try {
      const path =
        kind === "contract"
          ? `loans/${id}/contract/`
          : `loans/${id}/schedule-pdf/`;
      const filename =
        kind === "contract"
          ? `loan_contract_${loan_id}.pdf`
          : `loan_schedule_${loan_id}.pdf`;
      await downloadPdf(path, filename, { open: true });
    } catch (e) {
      message.error("Failed to download document. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const DocumentButtons = () =>
    contractAvailable ? (
      <div className="flex gap8" style={{ width: "100%" }}>
        <Button
          className="x100"
          type="default"
          icon={<DocumentTextOutline />}
          loading={downloading === "contract"}
          onClick={() => handleDownload("contract")}
        >
          Loan Contract
        </Button>
        <Button
          className="x100"
          type="default"
          icon={<DocumentTextOutline />}
          loading={downloading === "schedule"}
          onClick={() => handleDownload("schedule")}
        >
          Schedule
        </Button>
      </div>
    ) : null;

  // Device finance — direct user to Device Orders page
  if (isDevice && status === "approved") {
    return (
      <div className="flexColumn gap8 p15">
        <div className="font12 grey textCenter">
          This is a device finance loan. Go to{" "}
          <b>Device Orders</b> to pay your deposit and proceed.
        </div>
        <DocumentButtons />
      </div>
    );
  }

  // Regular loan approved — accept offer first
  if (status === "approved" && !offer_accepted) {
    return (
      <div className="flexColumn gap8 p15">
        <Button
          className="x100"
          onClick={() =>
            openModalForm({ source: ACCEPT_OFFER, state: details })
          }
        >
          Review & Accept Offer
        </Button>
      </div>
    );
  }

  // Active / defaulted → repayment actions
  if (status === ACTIVE || status === "defaulted") {
    return (
      <div className="flexColumn gap8 p15">
        <Button
          className="x100"
          onClick={() => openModalForm({ source: REPAY, state: details })}
        >
          Make Repayment
        </Button>
        <Button
          className="x100"
          type="default"
          onClick={() =>
            openModalForm({ source: EARLY_SETTLE, state: details })
          }
        >
          Early Settlement
        </Button>
        <DocumentButtons />
      </div>
    );
  }

  // Completed/paid/funded — just document downloads
  if (contractAvailable) {
    return (
      <div className="flexColumn gap8 p15">
        <DocumentButtons />
      </div>
    );
  }

  return null;
};